<template>
    <div class="ui-page">
        <ui-page-header title="Procuração modelo" back="/procuradores" back-text="Procuradores"
            subtitle="Texto da procuração que o procurador gera já preenchida para o credor assinar. Credor pessoa física assinando pelo GOV.br é aprovado automaticamente; pessoa jurídica segue para análise.">
        </ui-page-header>

        <!-- Escopo: modelo geral ou de um caso -->
        <section class="ui-card pt-scope">
            <div class="pt-scope__row">
                <div class="pt-scope__field">
                    <label class="pt-label">Modelo</label>
                    <v-autocomplete v-model="clientId" :items="scopeOptions" item-value="id" item-text="name" outlined dense hide-details
                        placeholder="Modelo geral (todos os casos)" clearable @change="onScopeChange"></v-autocomplete>
                </div>
                <div class="pt-scope__hint">
                    <template v-if="!clientId">O modelo geral vale para todos os casos que não têm modelo próprio.</template>
                    <template v-else-if="!versions.length">Este caso ainda usa o <strong>modelo geral</strong>. Crie uma versão para ter texto próprio.</template>
                    <template v-else>Este caso tem modelo próprio: ele substitui o geral.</template>
                </div>
            </div>
        </section>

        <div class="pt-grid">
            <!-- Versoes -->
            <section class="ui-card pt-versions">
                <div class="pt-versions__head">
                    <div class="pt-section-title">Versões</div>
                    <v-btn v-if="$allowed('create') && !hasDraft" small depressed color="primary" :loading="busy === 'new'" @click="newDraft(null)">
                        <v-icon left small>mdi-plus</v-icon>Nova versão
                    </v-btn>
                </div>
                <v-progress-linear v-if="loading" indeterminate color="primary" />
                <ui-empty v-if="!loading && !versions.length" icon="mdi-file-sign" text="Nenhuma versão ainda." />
                <button v-for="row in versions" :key="row.id" type="button" class="pt-version" :class="{ 'pt-version--active': selected && selected.id === row.id }" @click="select(row.id)">
                    <div class="pt-version__top">
                        <span class="pt-version__number">v{{ row.version }}</span>
                        <ui-badge :text="STATUS[row.status].text" :tone="STATUS[row.status].tone" />
                    </div>
                    <div class="pt-version__title">{{ row.title }}</div>
                    <div class="pt-version__meta">
                        <template v-if="row.published_at">publicada {{ row.published_at | defaultDate("DD/MM/YYYY") }}</template>
                        <template v-else>criada {{ row.created_at | defaultDate("DD/MM/YYYY") }}</template>
                        · {{ row.documents }} emitida(s)
                    </div>
                </button>
            </section>

            <!-- Versao selecionada -->
            <section class="ui-card pt-editor">
                <ui-empty v-if="!selected" icon="mdi-file-document-edit-outline" text="Selecione uma versão ou crie uma nova." />
                <template v-else>
                    <div class="pt-editor__head">
                        <div>
                            <div class="pt-section-title">Versão {{ selected.version }}</div>
                            <div class="pt-muted">{{ STATUS[selected.status].hint }}</div>
                        </div>
                        <div class="pt-editor__actions">
                            <v-btn small outlined :loading="busy === 'preview'" @click="preview"><v-icon left small>mdi-file-pdf-box</v-icon>Prévia PDF</v-btn>
                            <template v-if="isDraft">
                                <v-btn v-if="$allowed('delete')" small text color="error" :disabled="!!busy" @click="removeDraft">Excluir rascunho</v-btn>
                                <v-btn v-if="$allowed('edit')" small outlined color="primary" :loading="busy === 'save'" @click="save">Salvar</v-btn>
                                <v-btn v-if="$allowed('edit')" small depressed color="primary" :loading="busy === 'publish'" @click="publish"><v-icon left small>mdi-publish</v-icon>Publicar</v-btn>
                            </template>
                            <v-btn v-else-if="$allowed('create') && !hasDraft" small outlined color="primary" :loading="busy === 'new'" @click="newDraft(selected.id)">
                                <v-icon left small>mdi-content-copy</v-icon>Nova versão a partir desta
                            </v-btn>
                        </div>
                    </div>

                    <div v-if="isDraft && errors.length" class="ui-suggestion ui-suggestion--warn mb-3">
                        <div v-for="error in errors" :key="error">{{ error }}</div>
                    </div>

                    <label class="pt-label">Título</label>
                    <v-text-field v-model="form.title" :disabled="!isDraft" outlined dense hide-details class="mb-3" maxlength="255" />
                    <label class="pt-label">Texto da procuração</label>
                    <plan-adhesion-editor v-model="form.content_html" :catalog="catalog" :disabled="!isDraft" :min-height="380" />
                    <p class="pt-muted mt-2 mb-0">
                        Campos obrigatórios: nome e CPF/CNPJ do credor e nome do procurador. O rodapé com o código da procuração e a orientação de assinatura pelo GOV.br entram automaticamente.
                    </p>
                </template>
            </section>
        </div>
    </div>
</template>

<script>
import axios from "axios";
import PlanAdhesionEditor from "@/components/plan-adhesion/PlanAdhesionEditor.vue";

const STATUS = {
    draft: { text: "Rascunho", tone: "warning", hint: "Rascunho: edite e publique. Ainda não é usado pelos procuradores." },
    published: { text: "Publicada", tone: "success", hint: "Versão em uso: as novas procurações saem com este texto." },
    archived: { text: "Arquivada", tone: "neutral", hint: "Versão anterior: procurações já emitidas com ela continuam válidas." },
};

// Procuracao modelo (geral ou por caso) com versoes.
export default {
    name: "ProcurationTemplates",

    components: { PlanAdhesionEditor },

    data: () => ({
        STATUS,
        loading: false,
        busy: null,
        clientId: null,
        clients: [],
        clientsWithTemplate: [],
        versions: [],
        catalog: [],
        selected: null,
        errors: [],
        form: { title: "", content_html: "" },
    }),

    computed: {
        isDraft() {
            return this.selected && this.selected.status === "draft";
        },
        hasDraft() {
            return this.versions.some((row) => row.status === "draft");
        },
        // Casos com modelo proprio primeiro.
        scopeOptions() {
            const own = new Set(this.clientsWithTemplate.map((row) => Number(row.id)));
            return this.clients
                .map((client) => ({ id: client.id, name: own.has(Number(client.id)) ? `${client.name} · modelo próprio` : client.name, own: own.has(Number(client.id)) }))
                .sort((a, b) => Number(b.own) - Number(a.own) || a.name.localeCompare(b.name));
        },
    },

    async created() {
        this.loadCatalog();
        this.loadClients();
        await this.load();
    },

    methods: {
        async loadCatalog() {
            try {
                const result = await axios.get("/api/admin/procurators/procuration-templates/catalog");
                this.catalog = result.data.content;
            } catch (error) {
                this.notifyError(error);
            }
        },

        async loadClients() {
            try {
                const result = await axios.get("/api/clients", { params: { limit: 100000 } });
                this.clients = (result.data.content && result.data.content.data) || [];
            } catch (error) {
                this.clients = [];
            }
        },

        async load(selectId = null) {
            this.loading = true;
            try {
                const result = await axios.get("/api/admin/procurators/procuration-templates", { params: { client_id: this.clientId || undefined } });
                const content = result.data.content;
                this.versions = content.data;
                this.clientsWithTemplate = content.clients_with_template || [];
                const target = selectId || (this.versions.find((row) => row.status === "draft") || this.versions.find((row) => row.status === "published") || this.versions[0] || {}).id;
                if (target) await this.select(target);
                else this.selected = null;
            } catch (error) {
                this.notifyError(error);
            } finally {
                this.loading = false;
            }
        },

        onScopeChange() {
            this.selected = null;
            this.load();
        },

        async select(id) {
            try {
                const result = await axios.get(`/api/admin/procurators/procuration-templates/${id}`);
                this.selected = result.data.content;
                this.errors = this.selected.errors || [];
                this.form = { title: this.selected.title, content_html: this.selected.content_html || "" };
            } catch (error) {
                this.notifyError(error);
            }
        },

        async newDraft(fromId) {
            this.busy = "new";
            try {
                const result = await axios.post("/api/admin/procurators/procuration-templates", { client_id: this.clientId || null, from_template_id: fromId || undefined });
                await this.load(result.data.content.id);
                this.$dialog.notify.success(`Rascunho da versão ${result.data.content.version} criado.`, { position: "top-right", timeout: 3000 });
            } catch (error) {
                this.notifyError(error);
            } finally {
                this.busy = null;
            }
        },

        async save(silent = false) {
            this.busy = "save";
            try {
                const result = await axios.patch(`/api/admin/procurators/procuration-templates/${this.selected.id}`, this.form);
                this.selected = result.data.content;
                this.errors = this.selected.errors || [];
                if (silent !== true) this.$dialog.notify.success("Rascunho salvo.", { position: "top-right", timeout: 2500 });
                return true;
            } catch (error) {
                this.notifyError(error);
                return false;
            } finally {
                this.busy = null;
            }
        },

        async publish() {
            if (!(await this.save(true))) return;
            if (this.errors.length) {
                this.$dialog.notify.warning("Corrija os itens indicados antes de publicar.", { position: "top-right", timeout: 4000 });
                return;
            }
            const proceed = await this.$dialog.confirm({
                title: `Publicar versão ${this.selected.version}`,
                text: "As novas procurações passam a sair com este texto. As já emitidas continuam valendo com a versão em que foram geradas.",
                actions: { false: "Cancelar", true: "Publicar" },
            });
            if (!proceed) return;
            this.busy = "publish";
            try {
                await axios.post(`/api/admin/procurators/procuration-templates/${this.selected.id}/publish`);
                this.$dialog.notify.success("Versão publicada.", { position: "top-right", timeout: 3000 });
                await this.load(this.selected.id);
            } catch (error) {
                this.notifyError(error);
            } finally {
                this.busy = null;
            }
        },

        async removeDraft() {
            const proceed = await this.$dialog.confirm({ title: "Excluir rascunho", text: "O rascunho será excluído.", actions: { false: "Cancelar", true: "Excluir" } });
            if (!proceed) return;
            this.busy = "remove";
            try {
                await axios.delete(`/api/admin/procurators/procuration-templates/${this.selected.id}`);
                this.selected = null;
                await this.load();
            } catch (error) {
                this.notifyError(error);
            } finally {
                this.busy = null;
            }
        },

        // Previa com dados de exemplo; o rascunho e salvo antes.
        async preview() {
            if (this.isDraft && !(await this.save(true))) return;
            this.busy = "preview";
            try {
                const result = await axios.get(`/api/admin/procurators/procuration-templates/${this.selected.id}/preview`, { responseType: "blob" });
                const url = URL.createObjectURL(new Blob([result.data], { type: "application/pdf" }));
                window.open(url, "_blank");
                setTimeout(() => URL.revokeObjectURL(url), 60000);
            } catch (error) {
                this.notifyError(error);
            } finally {
                this.busy = null;
            }
        },

        notifyError(error) {
            this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 5000 });
        },
    },
};
</script>

<style scoped>
.pt-scope { padding: 16px 20px; margin-bottom: 16px; }
.pt-scope__row { display: flex; align-items: flex-end; gap: 20px; flex-wrap: wrap; }
.pt-scope__field { width: 420px; max-width: 100%; }
.pt-scope__hint { font-size: 13px; color: #667085; padding-bottom: 10px; }
.pt-label { display: block; font-size: 12.5px; font-weight: 500; color: #344054; margin-bottom: 4px; }
.pt-section-title { font-size: 14px; font-weight: 600; color: #101828; }
.pt-muted { font-size: 12.5px; color: #667085; }
.pt-grid { display: grid; grid-template-columns: 280px minmax(0, 1fr); gap: 16px; align-items: start; }
.pt-versions { padding: 14px; }
.pt-versions__head { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 10px; }
.pt-version { display: block; width: 100%; text-align: left; padding: 10px 12px; border: 1px solid #eaecf0; border-radius: 10px; background: #fff; margin-bottom: 8px; cursor: pointer; }
.pt-version:hover { border-color: #98a2b3; }
.pt-version--active { border-color: #27306b; box-shadow: 0 0 0 1px #27306b inset; }
.pt-version__top { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.pt-version__number { font-weight: 700; color: #101828; }
.pt-version__title { font-size: 13px; color: #344054; margin-top: 4px; }
.pt-version__meta { font-size: 12px; color: #98a2b3; margin-top: 2px; }
.pt-editor { padding: 18px 20px; }
.pt-editor__head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; flex-wrap: wrap; margin-bottom: 14px; }
.pt-editor__actions { display: flex; gap: 8px; flex-wrap: wrap; }
@media (max-width: 960px) { .pt-grid { grid-template-columns: 1fr; } }
</style>
